import { useCallback, useEffect, useMemo } from 'react';
import { get } from 'lodash-es';
import { useIntl } from '@leagueplatform/locales';
import {
  EntityDetailError,
  findClosestAncestorPath,
  instancePathToFormPath,
} from '@web-config-app/core';
import {
  useLocales,
  useConfigApp,
  useConfigUserFeedback,
} from '@web-config-app/core-react';
import {
  validateData,
  FormError,
  createConfigurableAjv,
} from '@web-config-app/validation';
import { useEntityDetailsProps } from '../use-entity-details-props/use-entity-details-props';

/**
 * Hook to validate all localized fields across all locales when showFormValidation is true.
 * This ensures that validation errors appear for all localized fields regardless of which
 * form slice is currently active.
 */
export const useLocalizedFieldsValidation = () => {
  const { locales } = useLocales();
  const { defaultLocale, frontendValidation } = useConfigApp();
  const ajv = useMemo(() => {
    const { allowed } = frontendValidation.uriSchemePolicy;
    return createConfigurableAjv(allowed);
  }, [frontendValidation.uriSchemePolicy]);
  const { filteredErrorKeywords, setAdditionalValidationErrors } =
    useConfigUserFeedback((state) => state);
  const { entityRootData, rootSchema, nodeMap } = useEntityDetailsProps();
  const { formatMessage } = useIntl();

  // Error formatting function using nodeMap like useFormatFormErrors
  const formatConfigErrors = useCallback(
    (formErrors: FormError[], localeCode?: string): EntityDetailError[] =>
      formErrors.map((error) => {
        let detail;
        if (error.message) {
          detail = error.propertyName
            ? `${formatMessage({ id: error.propertyName })} ${error.message}`
            : error.message;
        } else {
          detail = `${error.keyword} error: ${error.schemaPath}`;
        }

        // Add locale suffix for non-default locale errors
        if (localeCode) {
          detail = `${detail} (${localeCode})`;
        }

        return {
          detail,
          // Client-side validation: ancestor-walking only. No property-
          // name fallback — that's reserved for BE pointers (which can
          // be abbreviated relative to the schema layout). For
          // client-side errors a guess would just produce a misleading
          // link.
          path: findClosestAncestorPath(
            instancePathToFormPath(error.instancePath),
            (path) => nodeMap.has(path),
          ),
          fieldPath: error.instancePath, // Keep original field path for matching
          locale: localeCode,
        };
      }),
    [formatMessage, nodeMap],
  );

  const validateAllLocalizedFields = useCallback(() => {
    // Get all non-default locales that actually have translation data
    const availableTranslations = Object.keys(
      entityRootData?.attributes?.translations || {},
    );
    const nonDefaultLocales =
      locales?.filter(
        (locale) =>
          locale.code !== defaultLocale &&
          availableTranslations.includes(locale.code),
      ) || [];

    // Function to recursively find all localized fields in the schema
    const findLocalizedFields = (
      schema: any,
      path = '',
    ): Array<{ path: string; schema: any }> => {
      const localizedFields: Array<{ path: string; schema: any }> = [];

      if (schema?.type === 'object' && schema?.properties) {
        Object.entries(schema.properties).forEach(
          ([key, propSchema]: [string, any]) => {
            const fieldPath = path ? `${path}.${key}` : key;

            if (propSchema?.['x-entity-localized']) {
              localizedFields.push({ path: fieldPath, schema: propSchema });
            }

            // Recursively check nested objects
            if (propSchema?.type === 'object' && propSchema?.properties) {
              localizedFields.push(
                ...findLocalizedFields(propSchema, fieldPath),
              );
            }

            // Check array items
            if (propSchema?.type === 'array' && propSchema?.items) {
              if (propSchema.items['x-entity-localized']) {
                localizedFields.push({
                  path: fieldPath,
                  schema: propSchema.items,
                });
              }
              if (
                propSchema.items?.type === 'object' &&
                propSchema.items?.properties
              ) {
                localizedFields.push(
                  ...findLocalizedFields(propSchema.items, fieldPath),
                );
              }
            }
          },
        );
      }

      return localizedFields;
    };

    // Find all localized fields in the schema
    const localizedFields = findLocalizedFields(rootSchema);

    // Collect all validation errors into a single array to avoid multiple state updates
    const allLocalizedValidationErrors: EntityDetailError[] = [];

    // Validate each localized field for each non-default locale
    nonDefaultLocales.forEach((locale) => {
      const localeData =
        entityRootData?.attributes?.translations?.[locale.code]
          ?.domainEntityAttributes;

      if (!localeData) {
        return;
      }

      localizedFields.forEach(({ path, schema }) => {
        // Remove 'attributes.' prefix from path since localized data doesn't include it
        const localizedPath = path.startsWith('attributes.')
          ? path.slice('attributes.'.length)
          : path;

        // Handle array fields by checking if the path contains array items
        const pathParts = localizedPath.split('.');
        const arrayIndex = pathParts.findIndex((part) =>
          Array.isArray(
            get(
              localeData,
              pathParts.slice(0, pathParts.indexOf(part) + 1).join('.'),
            ),
          ),
        );

        if (arrayIndex !== -1) {
          // This is an array field - we need to validate each array item
          const arrayPath = pathParts.slice(0, arrayIndex + 1).join('.');
          const fieldPath = pathParts.slice(arrayIndex + 1).join('.');
          const arrayData = get(localeData, arrayPath);

          if (Array.isArray(arrayData)) {
            arrayData.forEach((item, index) => {
              const itemFieldData = get(item, fieldPath);

              if (
                itemFieldData !== undefined &&
                itemFieldData !== null &&
                itemFieldData !== ''
              ) {
                const { formErrors } = validateData({
                  schema,
                  data: itemFieldData,
                  filteredErrorKeywords: [...filteredErrorKeywords, 'required'],
                  ajv,
                });

                if (formErrors.length > 0) {
                  const errorsWithPath = formErrors.map((error) => ({
                    ...error,
                    instancePath: `/${path}/${index}`, // Include array index in path
                  }));

                  const formattedErrors = formatConfigErrors(
                    errorsWithPath,
                    locale.code,
                  );
                  allLocalizedValidationErrors.push(...formattedErrors);
                }
              }
            });
          }
        } else {
          // Non-array field - use original logic
          const fieldData = get(localeData, localizedPath);

          // Skip validation if field has no data
          if (
            fieldData === undefined ||
            fieldData === null ||
            fieldData === ''
          ) {
            return;
          }

          const { formErrors } = validateData({
            schema,
            data: fieldData,
            filteredErrorKeywords: [...filteredErrorKeywords, 'required'],
            ajv,
          });

          if (formErrors.length > 0) {
            const errorsWithPath = formErrors.map((error) => ({
              ...error,
              instancePath: `/${path}`, // Synthesize an Ajv-style path for instancePathToFormPath
            }));

            const formattedErrors = formatConfigErrors(
              errorsWithPath,
              locale.code,
            );
            allLocalizedValidationErrors.push(...formattedErrors);
          }
        }
      });
    });

    // Set all localized validation errors with a single key to prevent multiple state updates
    setAdditionalValidationErrors(
      'localized-fields-validation',
      allLocalizedValidationErrors,
    );
  }, [
    entityRootData?.attributes?.translations,
    locales,
    rootSchema,
    setAdditionalValidationErrors,
    defaultLocale,
    filteredErrorKeywords,
    ajv,
    formatConfigErrors,
  ]);

  // Cleanup validation errors when component unmounts
  useEffect(
    () => () => {
      setAdditionalValidationErrors('localized-fields-validation', []);
    },
    [setAdditionalValidationErrors],
  );

  return {
    validateAllLocalizedFields,
  };
};
